import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface SplashScreenProps {
    onComplete?: () => void;
    duration?: number; // Duration in ms before auto-complete
}

export function SplashScreen({ onComplete, duration = 2500 }: SplashScreenProps) {
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(false);
            setTimeout(() => {
                onComplete?.();
            }, 500); // Wait for exit animation
        }, duration);

        return () => clearTimeout(timer);
    }, [duration, onComplete]);

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-800"
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                >
                    {/* Animated background ripples */}
                    <div className="absolute inset-0 overflow-hidden">
                        {[...Array(3)].map((_, i) => (
                            <motion.div
                                key={i}
                                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white/20"
                                initial={{ width: 100, height: 100, opacity: 0.6 }}
                                animate={{
                                    width: [100, 600],
                                    height: [100, 600],
                                    opacity: [0.6, 0],
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    delay: i * 0.6,
                                    ease: 'easeOut',
                                }}
                            />
                        ))}
                    </div>

                    {/* Logo container with animation */}
                    <motion.div
                        className="relative z-10 flex flex-col items-center gap-6"
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{
                            duration: 0.6,
                            ease: [0.16, 1, 0.3, 1],
                        }}
                    >
                        {/* Logo with glow effect */}
                        <motion.div
                            className="relative"
                            animate={{
                                y: [0, -8, 0],
                            }}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: 'easeInOut',
                            }}
                        >
                            {/* Glow effect behind logo */}
                            <div className="absolute inset-0 blur-2xl bg-white/30 rounded-full scale-150" />

                            {/* Logo image */}
                            <motion.img
                                src="/icons/ripplefy-logo.png"
                                alt="Ripplefy"
                                className="relative w-32 h-32 md:w-40 md:h-40 object-contain drop-shadow-2xl"
                                initial={{ rotate: -10 }}
                                animate={{ rotate: 0 }}
                                transition={{ duration: 0.5, ease: 'easeOut' }}
                            />
                        </motion.div>

                        {/* App name with typing effect */}
                        <motion.div
                            className="text-center"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.5 }}
                        >
                            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
                                Ripplefy
                            </h1>
                            <motion.p
                                className="text-white/70 text-sm md:text-base mt-2"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.6, duration: 0.5 }}
                            >
                                Restaurant Management
                            </motion.p>
                        </motion.div>

                        {/* Loading indicator */}
                        <motion.div
                            className="flex gap-1 mt-4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.8 }}
                        >
                            {[0, 1, 2].map((i) => (
                                <motion.div
                                    key={i}
                                    className="w-2 h-2 bg-white/80 rounded-full"
                                    animate={{
                                        y: [0, -8, 0],
                                        opacity: [0.5, 1, 0.5],
                                    }}
                                    transition={{
                                        duration: 0.6,
                                        repeat: Infinity,
                                        delay: i * 0.15,
                                    }}
                                />
                            ))}
                        </motion.div>
                    </motion.div>

                    {/* Bottom wave decoration */}
                    <motion.div
                        className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/20 to-transparent"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                    />
                </motion.div>
            )}
        </AnimatePresence>
    );
}

// Hook to manage splash screen state - only shows in PWA mode
export function useSplashScreen(duration: number = 2500) {
    const [showSplash, setShowSplash] = useState(false);
    const [hasShown, setHasShown] = useState(false);
    const [isPWA, setIsPWA] = useState(false);

    useEffect(() => {
        // Check if running as installed PWA (standalone mode)
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches
            || (window.navigator as any).standalone === true // iOS Safari
            || document.referrer.includes('android-app://'); // Android TWA

        setIsPWA(isStandalone);

        // Only show splash in PWA mode and once per session
        if (isStandalone) {
            const splashShown = sessionStorage.getItem('splashShown');
            if (!splashShown) {
                setShowSplash(true);
            } else {
                setHasShown(true);
            }
        }
    }, []);

    const handleComplete = () => {
        setShowSplash(false);
        setHasShown(true);
        sessionStorage.setItem('splashShown', 'true');
    };

    return {
        showSplash: showSplash && isPWA && !hasShown,
        isPWA,
        SplashComponent: showSplash && isPWA && !hasShown ? (
            <SplashScreen onComplete={handleComplete} duration={duration} />
        ) : null,
    };
}
